import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ArrowUp, ArrowDown, Plus, Trash2, Loader2, ListOrdered, Save } from "lucide-react";

/**
 * Super Admin — Positions Manager.
 *
 * Positions are stored per-district (see backend `positions_col`) and their
 * `sequence` value drives sort order in Generate Sheet output. This screen
 * lets super admins pick a year (which resolves the district), then add,
 * rename, reorder (via up/down buttons) and delete positions without hitting
 * the API manually.
 */
export default function PositionsManager() {
    const [districts, setDistricts] = useState([]);
    const [selectedYid, setSelectedYid] = useState("");
    const [positions, setPositions] = useState([]);        // sorted by sequence asc
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState({});                  // per-id save/delete state
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);

    // Load district → year picker options
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/super/districts");
                const rows = Array.isArray(data) ? data : [];
                setDistricts(rows);
                // Auto-select the first year if nothing chosen yet
                if (!selectedYid) {
                    const firstYid = rows[0]?.years?.[0]?.id || "";
                    if (firstYid) setSelectedYid(firstYid);
                }
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed to load districts");
            }
        })();
    }, []);

    const yearOptions = useMemo(() => {
        const opts = [];
        for (const d of districts) {
            for (const y of (d.years || [])) {
                opts.push({
                    yid: y.id,
                    year: y.year,
                    district_number: d.district_number,
                    district_name: d.display_name,
                });
            }
        }
        return opts;
    }, [districts]);

    const loadPositions = useCallback(async () => {
        if (!selectedYid) { setPositions([]); return; }
        setLoading(true);
        try {
            const { data } = await api.get(`/scoped/district-years/${selectedYid}/positions`);
            const rows = Array.isArray(data) ? data : (data?.items || []);
            rows.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
            setPositions(rows);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load positions");
        } finally {
            setLoading(false);
        }
    }, [selectedYid]);

    useEffect(() => { loadPositions(); }, [loadPositions]);

    const create = async () => {
        const name = newName.trim();
        if (!name) return;
        setCreating(true);
        try {
            const nextSeq = (positions.length ? Math.max(...positions.map((p) => p.sequence || 0)) : 0) + 10;
            await api.post(`/scoped/district-years/${selectedYid}/positions`, {
                name, sequence: nextSeq,
            });
            toast.success(`Added "${name}"`);
            setNewName("");
            await loadPositions();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        } finally {
            setCreating(false);
        }
    };

    const renameRow = async (row, nextName) => {
        const name = (nextName || "").trim();
        if (!name || name === row.name) return;
        setBusy((s) => ({ ...s, [row.id]: "renaming" }));
        try {
            await api.patch(`/scoped/positions/${row.id}`, { name, sequence: row.sequence });
            toast.success(`Renamed to "${name}"`);
            await loadPositions();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Rename failed");
        } finally {
            setBusy((s) => { const c = { ...s }; delete c[row.id]; return c; });
        }
    };

    const swapSequence = async (idx, dir) => {
        const other = positions[idx + dir];
        const row = positions[idx];
        if (!row || !other) return;
        const rowSeq = row.sequence ?? 0;
        const otherSeq = other.sequence ?? 0;
        setBusy((s) => ({ ...s, [row.id]: "moving", [other.id]: "moving" }));
        try {
            await Promise.all([
                api.patch(`/scoped/positions/${row.id}`, { name: row.name, sequence: otherSeq }),
                api.patch(`/scoped/positions/${other.id}`, { name: other.name, sequence: rowSeq }),
            ]);
            await loadPositions();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reorder failed");
        } finally {
            setBusy((s) => { const c = { ...s }; delete c[row.id]; delete c[other.id]; return c; });
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete position "${row.name}"? This cannot be undone. Existing member roles that reference this position label will not be re-linked.`)) return;
        setBusy((s) => ({ ...s, [row.id]: "deleting" }));
        try {
            await api.delete(`/scoped/positions/${row.id}`);
            toast.success(`Deleted "${row.name}"`);
            await loadPositions();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally {
            setBusy((s) => { const c = { ...s }; delete c[row.id]; return c; });
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-rotary-cream via-white to-rotary-cream/40">
            <header className="max-w-5xl mx-auto px-6 pt-8 pb-4">
                <Link to="/admin/platform" className="inline-flex items-center gap-1 text-sm text-rotary-blue hover:underline" data-testid="positions-back-link">
                    <ChevronLeft className="h-4 w-4" /> Back to platform
                </Link>
                <h1 className="rotary-heading text-3xl sm:text-4xl mt-3 flex items-center gap-2">
                    <ListOrdered className="h-6 w-6 text-rotary-blue" /> Positions Manager
                </h1>
                <p className="text-sm text-muted-foreground max-w-2xl mt-2">
                    Add, rename, reorder and delete the position labels that Generate Sheet uses to sort rows within a committee. The sequence number controls the print order (lower = earlier).
                </p>
            </header>

            <main className="max-w-5xl mx-auto px-6 pb-16 space-y-5">
                <Card className="p-5">
                    <div className="grid gap-3 sm:grid-cols-[minmax(240px,1fr)_auto] items-end">
                        <div>
                            <label className="label-eyebrow text-[11px]">Year</label>
                            <Select value={selectedYid} onValueChange={setSelectedYid}>
                                <SelectTrigger className="mt-1" data-testid="positions-year-select">
                                    <SelectValue placeholder="Choose a year…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {yearOptions.map((o) => (
                                        <SelectItem key={o.yid} value={o.yid} data-testid={`positions-year-option-${o.yid}`}>
                                            {o.year} · {o.district_number} {o.district_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <span className="text-xs text-muted-foreground" data-testid="positions-count">
                            {loading ? "Loading…" : `${positions.length} position${positions.length === 1 ? "" : "s"}`}
                        </span>
                    </div>
                </Card>

                <Card className="p-0 overflow-hidden">
                    <div className="grid grid-cols-[70px_1fr_auto] gap-3 px-4 py-3 border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <div>Seq</div>
                        <div>Position name</div>
                        <div className="text-right">Actions</div>
                    </div>
                    {loading ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">
                            <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Loading…
                        </div>
                    ) : positions.length === 0 ? (
                        <div className="p-8 text-center text-sm text-muted-foreground" data-testid="positions-empty">
                            No positions defined for this district yet. Add one below.
                        </div>
                    ) : (
                        <ul className="divide-y" data-testid="positions-list">
                            {positions.map((row, idx) => (
                                <PositionRow
                                    key={row.id}
                                    row={row}
                                    idx={idx}
                                    total={positions.length}
                                    busy={busy[row.id]}
                                    onRename={renameRow}
                                    onMoveUp={() => swapSequence(idx, -1)}
                                    onMoveDown={() => swapSequence(idx, +1)}
                                    onDelete={() => remove(row)}
                                />
                            ))}
                        </ul>
                    )}
                    <div className="border-t bg-muted/20 p-4 flex flex-wrap items-center gap-3">
                        <Input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") create(); }}
                            placeholder="New position name (e.g. Director)"
                            className="max-w-sm"
                            disabled={!selectedYid || creating}
                            data-testid="positions-new-name-input"
                        />
                        <Button
                            onClick={create}
                            disabled={!newName.trim() || !selectedYid || creating}
                            className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                            data-testid="positions-add-btn"
                        >
                            {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
                            Add position
                        </Button>
                    </div>
                </Card>
            </main>
        </div>
    );
}

function PositionRow({ row, idx, total, busy, onRename, onMoveUp, onMoveDown, onDelete }) {
    const [draft, setDraft] = useState(row.name || "");
    useEffect(() => { setDraft(row.name || ""); }, [row.name]);
    const dirty = draft.trim() !== (row.name || "").trim();
    return (
        <li className="grid grid-cols-[70px_1fr_auto] gap-3 items-center px-4 py-2.5" data-testid={`position-row-${row.id}`}>
            <div className="tabular-nums text-sm text-muted-foreground">
                {row.sequence ?? "—"}
            </div>
            <div className="flex items-center gap-2 min-w-0">
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => dirty && onRename(row, draft)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && dirty) onRename(row, draft);
                        if (e.key === "Escape") setDraft(row.name || "");
                    }}
                    className="max-w-md"
                    data-testid={`position-name-${row.id}`}
                />
                {dirty && (
                    <Button size="sm" variant="ghost" onClick={() => onRename(row, draft)} data-testid={`position-save-${row.id}`}>
                        <Save className="h-3.5 w-3.5" />
                    </Button>
                )}
            </div>
            <div className="flex items-center justify-end gap-1">
                <Button
                    size="icon"
                    variant="ghost"
                    disabled={idx === 0 || !!busy}
                    onClick={onMoveUp}
                    title="Move up"
                    data-testid={`position-up-${row.id}`}
                >
                    <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                    size="icon"
                    variant="ghost"
                    disabled={idx === total - 1 || !!busy}
                    onClick={onMoveDown}
                    title="Move down"
                    data-testid={`position-down-${row.id}`}
                >
                    <ArrowDown className="h-4 w-4" />
                </Button>
                <Button
                    size="icon"
                    variant="ghost"
                    disabled={!!busy}
                    onClick={onDelete}
                    className="text-rotary-cranberry hover:bg-rotary-cranberry/10"
                    title="Delete"
                    data-testid={`position-delete-${row.id}`}
                >
                    {busy === "deleting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </Button>
            </div>
        </li>
    );
}
